import React, { useState, useCallback, useEffect, useRef } from "react"
import { Line, Bar, HorizontalBar, defaults } from "react-chartjs-2"
import Modal from "./modal"
import {
  buildStackedBarChartData,
  buildBarChartData,
  formatSource,
} from "../helpers/utils"
import Table from "./table"

defaults.global.defaultFontFamily = "Prompt"
defaults.global.defaultFontSize = 13
defaults.maintainAspectRatio = false

const pluginPadding = {
  id: "paddingBelowLegends",
  beforeInit: function (chart, options) {
    chart.legend.afterFit = function () {
      this.height = this.height + 20
    }
  },
}

export default function Chart({
  chart,
  chartType = "vertical",
  children,
  hideTitle,
}) {
  const [viewAsTable, setViewAsTable] = useState(false)
  const {
    data,
    options,
    figureNumber,
    subTitle,
    title,
    caption,
    source,
    customLegend,
    type,
    layout,
  } = chart

  const chartRef = useRef(null)
  const buttonRef = useRef(null)

  const downHandler = useCallback(
    ({ key }) => {
      if ((key === "Escape" || key === "Esc") && viewAsTable) {
        setViewAsTable(false)
      }
    },
    [viewAsTable]
  )

  useEffect(() => {
    document.addEventListener("keydown", downHandler)
    // Remove event listeners on cleanup
    return () => {
      document.removeEventListener("keydown", downHandler)
    }
  }, [viewAsTable, downHandler])

  const tableData =
    type === "stacked"
      ? buildStackedBarChartData(chart)
      : buildBarChartData(chart)

  const [hiddenItems, setHiddenItems] = useState([])

  const handleLegendClick = datasetIndex => {
    if (hiddenItems.includes(datasetIndex)) {
      setHiddenItems(hiddenItems.filter(item => item !== datasetIndex))
    } else {
      setHiddenItems([...hiddenItems, datasetIndex])
    }
  }

  useEffect(() => {
    const chart = chartRef.current.chartInstance
    hiddenItems.forEach(item => {
      chart.getDatasetMeta(item).hidden = true
    })
    chart.update()
  }, [hiddenItems])

  const Legend = () => {
    return (
      <ul className="chart__legend">
        {chart.data.datasets.map(({ label, backgroundColor }, index) => {
          return (
            <li key={index}>
              <button
                onClick={() => handleLegendClick(index)}
                className={
                  hiddenItems.includes(index)
                    ? "chart__legend__item chart__legend__item--hidden"
                    : "chart__legend__item"
                }
                aria-label={
                  hiddenItems.includes(index)
                    ? `Show data for ${label}`
                    : `Hide data for ${label}`
                }
              >
                <span
                  className="chart__legend__item__dot"
                  style={{ color: backgroundColor }}
                >
                  ●
                </span>
                <span className="chart__legend__item__label">{label}</span>
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  const TableButton = () => (
    <button
      className="chart__toggle"
      onClick={() => setViewAsTable(true)}
      aria-label="View chart as table"
      ref={buttonRef}
    >
      <svg
        width={29}
        height={27}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5 2h12a3 3 0 0 1 3 3H2a3 3 0 0 1 3-3Zm17 5V5a5 5 0 0 0-5-5H5a5 5 0 0 0-5 5v12a5 5 0 0 0 5 5h8.07a7.954 7.954 0 0 1-.818-2H5a3 3 0 0 1-3-3V7h18v3c.69 0 1.36.088 2 .252V7Zm2.89 14.477a6 6 0 1 0-1.414 1.414l3.08 3.079a1 1 0 0 0 1.414-1.415l-3.08-3.078ZM6 11h10v-1H6v1Zm8 3H6v-1h8v1Zm-8 3h6v-1H6v1Zm18 1a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
          fill="#004C97"
        />
      </svg>
    </button>
  )

  const ChartType = ({ type }) => {
    switch (type) {
      case "horizontal":
        return (
          <HorizontalBar
            data={data}
            options={options}
            plugins={[pluginPadding]}
            ref={chartRef}
          />
        )
      case "line":
        return (
          <Line
            data={data}
            options={options}
            plugins={[pluginPadding]}
            ref={chartRef}
          />
        )
      default:
        return (
          <Bar
            data={data}
            options={options}
            plugins={[pluginPadding]}
            ref={chartRef}
          />
        )
    }
  }

  return (
    <figure role="group" className="chart">
      <div
        className={
          layout
            ? `chart__inner chart--bar chart--${layout}`
            : "chart__inner chart--bar"
        }
      >
        <header>
          {!hideTitle && (
            <div className="chart__intro">
              {figureNumber && (
                <div className="chart__figure-number">
                  Figure {figureNumber}
                </div>
              )}
              {title && <h3 className="chart__title">{formatSource(title)}</h3>}
              {subTitle &&
                (title ? (
                  <h4 className="chart__sub-title">{formatSource(subTitle)}</h4>
                ) : (
                  <h3 className="chart__sub-title">{formatSource(subTitle)}</h3>
                ))}
              {caption && (
                <figcaption>
                  {caption && (
                    <p className="chart__caption">{formatSource(caption)}</p>
                  )}
                </figcaption>
              )}
              {customLegend && <Legend />}
              {children && children}
            </div>
          )}
          {tableData && (
            <>
              <Modal
                isOpen={viewAsTable}
                closeModal={() => setViewAsTable(false)}
                buttonRef={buttonRef}
              >
                <Table
                  data={tableData}
                  figureNumber={figureNumber}
                  title={title}
                  subTitle={subTitle}
                  caption={caption}
                  source={source}
                />
              </Modal>
              <TableButton />
            </>
          )}
        </header>
        <div
          className={
            options.height
              ? "chart__container chart__container--" + options.height
              : "chart__container"
          }
          aria-label={`Chart showing ${title}`}
        >
          <ChartType type={chartType} />
        </div>
      </div>
      {source && <p className="chart__source">{formatSource(source)}</p>}
    </figure>
  )
}
